import { pick } from 'lodash';

import Locator, { LocatorSpec } from '../../shared/Locator';

import type { Project, Revision, RevisionLicense } from '../types';
import type { UpdateProjectCorrectionRequest } from '../../typings/api';

export const isDependencyCacheValid = (revision: Revision) => {
  if (!revision?.dependency_cache_updated || !revision?.dependency_cache_valid) {
    return false;
  }
  return new Date(revision.dependency_cache_updated) >= new Date(revision.dependency_cache_valid);
};

export const prepareRevisionDependencyForSaving = (
  locator: string,
  data: Pick<Project, 'title' | 'description' | 'url' | 'authors' | 'scm_url'> & {
    licenses?: Array<Array<Partial<RevisionLicense> & { licenseId: string }>>
    parentRevisionLocator?: string;
  },
): UpdateProjectCorrectionRequest & { dependencyProjectLocator: string } => {
  const project = pick(data, 'title', 'description', 'url', 'authors', 'scm_url');
  const revisionLocators = pick(data, 'parentRevisionLocator');
  let licenses: UpdateProjectCorrectionRequest['licenses'];

  if (data.licenses) {
    licenses = data.licenses.map(
      group => group.map(
        license => pick(
          license,
          'licenseId',
          'title',
          'url',
          'text',
          'copyright',
          'ignored',
          'licenseGroupId',
        ),
      ),
    );
  }

  const dependencyProjectLocator = Locator.parsePackageString(locator);

  return { dependencyProjectLocator, licenses, project, ...revisionLocators };
};

/**
 * When the locator's revision is a commit sha1, returns the first 6 characters.
 * When the revision is a version number or a tag, return as it is — can't be compacted.
 * When the locator doesn't have a revision return three dots.
 */
export function compactRevision(locator: LocatorSpec): string {
  const revision = Locator.parse(locator).revision || '...';
  return /^[0-9a-f]{40}$/.test(revision) ? revision.slice(0, 6) : revision;
}

/**
 * Returns whether the current revision is being analyzed.
 * This is determined by whether the revision is in a steady state.
 */
export function isAnalyzing(revision: Revision): boolean {
  return !isDependencyCacheValid(revision);
}
